import React, { useEffect, useRef, useState, ChangeEvent } from 'react';
import {
    Button,
    ButtonGroup,
    ButtonToolbar,
    Checkbox,
    Col,
    FormGroup,
    Modal,
    Row
} from 'react-bootstrap';
import isEqual from 'react-fast-compare';
import { Translate } from '../../../../shared/LanguageService';
import { ICompareFile, ICompareImagesStorey } from './CompareImages';
import { LocalDateTime } from '../../../../features/localDate/DateTimeView';
import FormSelect from '../../FormSelect';
import CustomerMessages from '../CustomerMessages';
import CompareImageCanvas from './CompareImagesCanvas';
import { usePrevious } from '../../../../shared/hooks/previousValue';

type CompareImagesModalProps = {
    storeys: ICompareImagesStorey[];
    selectedStoreyId: number;
    selectedStoreyFileType: number;
    onClose: () => void;
    onFilterChanged: (showEverything: boolean) => void;
    extraInformation?: string;
    returnedMessages?: {
        returnedMessage: string;
        returnedDate: LocalDateTime;
    }[];
    address: string;
};

type SelectStoreyProps = {
    storeys: ICompareImagesStorey[];
    onChange: (newStorey: ICompareImagesStorey | null) => void;
    value: string;
};

function getStoreyName(storey: ICompareImagesStorey): string {
    if (storey.storeyId > 0) {
        return Translate(storey.name);
    }

    return storey.name;
}

export function SelectStorey({ storeys, onChange, value }: SelectStoreyProps) {
    function handleChange(event: ChangeEvent) {
        let newStorey = storeys.find((storey) => storey.id.toString() === event.target.value);
        onChange(newStorey ? newStorey : null);
    }

    return (
        <FormSelect name="selectedStorey" value={value} onChange={handleChange}>
            {storeys.map((storey: ICompareImagesStorey) => (
                <option key={'selected-storey-' + storey.id} value={storey.id.toString()}>
                    {getStoreyName(storey)}
                </option>
            ))}
        </FormSelect>
    );
}

type ImageSelectProps = {
    value: string;
    onChange: (newImage: ICompareFile | null) => void;
    images: ICompareFile[];
    name: string;
};

function ImageSelect({ value, onChange, images, name }: ImageSelectProps) {
    function handleChange(event: ChangeEvent) {
        let image = images.find((i) => i.fileReference === event.target.value);
        onChange(image ? image : null);
    }

    return (
        <FormSelect name={name} value={value} onChange={handleChange}>
            {images.map((image: ICompareFile) => (
                <option key={image.fileReference} value={image.fileReference}>
                    {image.filename}
                </option>
            ))}
        </FormSelect>
    );
}

type SelectReferenceImageProps = {
    value: string;
    onChange: (newImage: ICompareFile | null) => void;
    images: ICompareFile[];
};

function SelectReferenceImage({ value, onChange, images }: SelectReferenceImageProps) {
    return <ImageSelect name="selectedLeft" value={value} onChange={onChange} images={images} />;
}

type SelectComparableImageProps = {
    value: string;
    onChange: (newImage: ICompareFile | null) => void;
    images: ICompareFile[];
};

function SelectComparableImage({ value, onChange, images }: SelectComparableImageProps) {
    return <ImageSelect value={value} onChange={onChange} images={images} name="selectedRight" />;
}

function isReferenceImage(image: ICompareFile): boolean {
    return image.defaultBlueprint;
}

function isComparableImage(image: ICompareFile): boolean {
    return image.defaultCompareImage;
}

type CanvasZoomButtonsProps = {
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomReset: () => void;
    onImageRotateClockWise: () => void;
    onImageRotateCounterClockWise: () => void;
};

function CanvasZoomButtons({
    onZoomIn,
    onZoomOut,
    onZoomReset,
    onImageRotateClockWise,
    onImageRotateCounterClockWise
}: CanvasZoomButtonsProps) {
    return (
        <ButtonToolbar className="center">
            <ButtonGroup>
                <Button bsStyle="success" onClick={onZoomIn}>
                    +
                </Button>
                <Button bsStyle="info" onClick={onZoomReset}>
                    1:1
                </Button>
                <Button bsStyle="warning" onClick={onZoomOut}>
                    -
                </Button>
            </ButtonGroup>
            <ButtonGroup>
                <Button bsStyle="default" onClick={onImageRotateCounterClockWise}>
                    <i className="material-icons md-18 no-label">rotate_left</i>
                </Button>
                <Button bsStyle="default" onClick={onImageRotateClockWise}>
                    <i className="material-icons md-18 no-label">rotate_right</i>
                </Button>
            </ButtonGroup>
        </ButtonToolbar>
    );
}

function getSelectedStorey(
    selectedStoreyId: number,
    storeys: ICompareImagesStorey[]
): ICompareImagesStorey | null {
    let selectedStorey = storeys.find((storey) => storey.id === selectedStoreyId);
    if (selectedStorey) {
        return selectedStorey;
    }

    if (storeys.length > 0) {
        return storeys[0];
    }

    return null;
}

function CompareImagesModal({
    storeys,
    selectedStoreyFileType,
    onFilterChanged,
    onClose,
    address,
    extraInformation,
    returnedMessages,
    selectedStoreyId
}: CompareImagesModalProps) {
    let selectedStorey = getSelectedStorey(selectedStoreyId, storeys);
    let [currentStorey, setCurrentStorey] = useState<ICompareImagesStorey | null>(selectedStorey);
    let [selectedReferenceImage, setSelectedReferenceImage] = useState<ICompareFile | null>(null);
    let [selectedComparableImage, setSelectedComparableImage] = useState<ICompareFile | null>(null);
    let [showEverything, setShowEverything] = useState<boolean>(false);

    let leftCanvas = useRef<CompareImageCanvas | null>(null);
    let rightCanvas = useRef<CompareImageCanvas | null>(null);

    function handleChangeStorey(newStorey: ICompareImagesStorey | null) {
        if (newStorey) {
            setCurrentStorey(newStorey);
            setSelectedReferenceImage(null);
            setSelectedComparableImage(null);
        }
    }

    function handleChangeReferenceImage(newImage: ICompareFile | null) {
        if (newImage) {
            setSelectedReferenceImage(newImage);
        }
    }

    function handleChangeComparableImage(newImage: ICompareFile | null) {
        if (newImage) {
            setSelectedComparableImage(newImage);
        }
    }

    function handleChangeShowEverything(event: ChangeEvent) {
        setShowEverything(event.target.checked);
    }

    let previousShowEverything = usePrevious<boolean>(showEverything);
    useEffect(() => {
        if (previousShowEverything !== undefined && previousShowEverything !== showEverything) {
            onFilterChanged(showEverything);
        }
    }, [showEverything]);

    useEffect(() => {
        if (!isEqual(currentStorey, selectedStorey)) {
            setCurrentStorey(selectedStorey);
        }
    }, [selectedStorey]);

    useEffect(() => {
        if (!selectedReferenceImage) {
            let referenceImages = currentStorey.files.filter(isReferenceImage);
            let selectedImage =
                referenceImages.length > 1
                    ? referenceImages.find((image) => image.fileType === selectedStoreyFileType)
                    : referenceImages[0];
            setSelectedReferenceImage(selectedImage);
        }

        if (!selectedComparableImage) {
            let comparableImages = currentStorey.files.filter(isComparableImage);
            let selectedImage =
                comparableImages.length > 1
                    ? comparableImages.find((image) => image.fileType === selectedStoreyFileType)
                    : comparableImages[0];
            setSelectedComparableImage(selectedImage);
        }
    }, [currentStorey, selectedStoreyFileType]);

    return (
        <Modal show={true} onHide={onClose} bsSize="large" className="plan-compare-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    {Translate('view-order/admin-planorder/compare/title')}
                    {address ? ' - ' + address : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="tabular side-padding stretch form-row">
                    <Col className="col-xs-6">
                        <SelectStorey
                            value={
                                currentStorey && currentStorey.id ? currentStorey.id.toString() : ''
                            }
                            onChange={handleChangeStorey}
                            storeys={storeys}
                        />
                    </Col>
                    <Col className="col">
                        {(extraInformation ||
                            (returnedMessages && returnedMessages.length > 0)) && (
                            <CustomerMessages
                                extraInformation={extraInformation}
                                returnedMessages={returnedMessages}
                            />
                        )}
                        <FormGroup>
                            <Checkbox
                                name="showEverything-checbox"
                                checked={showEverything}
                                onChange={handleChangeShowEverything}
                            >
                                <span>
                                    {Translate(
                                        'view-order/admin-planorder/compare/show-everything'
                                    )}
                                </span>
                            </Checkbox>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <SelectReferenceImage
                            value={
                                selectedReferenceImage && selectedReferenceImage.fileReference
                                    ? selectedReferenceImage.fileReference
                                    : ''
                            }
                            onChange={handleChangeReferenceImage}
                            images={currentStorey.files}
                        />
                        <div className="zoom-item-container">
                            <CompareImageCanvas
                                id="leftCanvas"
                                image={
                                    selectedReferenceImage && selectedReferenceImage.fileReference
                                        ? `/api/file/${selectedReferenceImage.fileReference}`
                                        : ''
                                }
                                ref={(canvas) => {
                                    leftCanvas.current = canvas;
                                }}
                            />
                        </div>
                        <CanvasZoomButtons
                            onZoomIn={() => {
                                if (leftCanvas.current) {
                                    leftCanvas.current.zoom(1);
                                }
                            }}
                            onZoomOut={() => {
                                if (leftCanvas.current) {
                                    leftCanvas.current.zoom(-1);
                                }
                            }}
                            onZoomReset={() => {
                                if (leftCanvas.current) {
                                    leftCanvas.current.resetImage();
                                }
                            }}
                            onImageRotateClockWise={() => {
                                if (leftCanvas.current) {
                                    leftCanvas.current.rotate('cw');
                                }
                            }}
                            onImageRotateCounterClockWise={() => {
                                if (leftCanvas.current) {
                                    leftCanvas.current.rotate('ccw');
                                }
                            }}
                        />
                    </Col>
                    <Col md={6}>
                        <SelectComparableImage
                            value={
                                selectedComparableImage && selectedComparableImage.fileReference
                                    ? selectedComparableImage.fileReference
                                    : ''
                            }
                            onChange={handleChangeComparableImage}
                            images={currentStorey.files}
                        />
                        <div className="zoom-item-container">
                            <CompareImageCanvas
                                id="rightCanvas"
                                image={
                                    selectedComparableImage && selectedComparableImage.fileReference
                                        ? `/api/file/${selectedComparableImage.fileReference}`
                                        : ''
                                }
                                ref={(canvas) => {
                                    rightCanvas.current = canvas;
                                }}
                            />
                        </div>
                        <CanvasZoomButtons
                            onZoomIn={() => {
                                if (rightCanvas.current) {
                                    rightCanvas.current.zoom(1);
                                }
                            }}
                            onZoomOut={() => {
                                if (rightCanvas.current) {
                                    rightCanvas.current.zoom(-1);
                                }
                            }}
                            onZoomReset={() => {
                                if (rightCanvas.current) {
                                    rightCanvas.current.resetImage();
                                }
                            }}
                            onImageRotateClockWise={() => {
                                if (rightCanvas.current) {
                                    rightCanvas.current.rotate('cw');
                                }
                            }}
                            onImageRotateCounterClockWise={() => {
                                if (rightCanvas.current) {
                                    rightCanvas.current.rotate('ccw');
                                }
                            }}
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onClose}>
                    {Translate('view-order/admin-planorder/compare/close-modal')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default CompareImagesModal;
